/**
 * Role-Based Access Control (RBAC) Permissions
 *
 * DB Role Mapping:
 *   superadmin → full access (same as admin)
 *   admin      → Admin: full access to all modules
 *   direktur   → Direktur: view all + approve + all reports, no user management, no add/edit/delete
 *   manager    → Proyek Manager: view + approve + add/edit, no delete/user-management
 *   staff      → Viewer: view only, no edit/delete/approve
 */

export type AppRole = 'superadmin' | 'admin' | 'direktur' | 'manager' | 'staff';

export interface RolePermissions {
  // Screen-level access
  canViewDashboard: boolean;
  canViewMasterProject: boolean;
  canViewHutang: boolean;
  canViewPiutang: boolean;
  canViewPembayaran: boolean;
  canViewPenerimaan: boolean;
  canViewLaporan: boolean;
  canViewLaporanLanjutan: boolean;
  canViewLogAktivitas: boolean;
  canViewUserManagement: boolean;

  // Action-level access
  canAdd: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canApprove: boolean;
  canBayar: boolean;
  canTerima: boolean;
  canManageUsers: boolean;
}

const PERMISSIONS: Record<AppRole, RolePermissions> = {
  superadmin: {
    canViewDashboard: true,
    canViewMasterProject: true,
    canViewHutang: true,
    canViewPiutang: true,
    canViewPembayaran: true,
    canViewPenerimaan: true,
    canViewLaporan: true,
    canViewLaporanLanjutan: true,
    canViewLogAktivitas: true,
    canViewUserManagement: true,
    canAdd: true,
    canEdit: true,
    canDelete: true,
    canApprove: true,
    canBayar: true,
    canTerima: true,
    canManageUsers: true,
  },
  admin: {
    canViewDashboard: true,
    canViewMasterProject: true,
    canViewHutang: true,
    canViewPiutang: true,
    canViewPembayaran: true,
    canViewPenerimaan: true,
    canViewLaporan: true,
    canViewLaporanLanjutan: true,
    canViewLogAktivitas: true,
    canViewUserManagement: true,
    canAdd: true,
    canEdit: true,
    canDelete: true,
    canApprove: true,
    canBayar: true,
    canTerima: true,
    canManageUsers: true,
  },
  direktur: {
    // Direktur: view all + approve + all reports — cannot add/edit/delete or manage users
    canViewDashboard: true,
    canViewMasterProject: true,
    canViewHutang: true,
    canViewPiutang: true,
    canViewPembayaran: true,
    canViewPenerimaan: true,
    canViewLaporan: true,
    canViewLaporanLanjutan: true,
    canViewLogAktivitas: true,
    canViewUserManagement: false,
    canAdd: false,
    canEdit: false,
    canDelete: false,
    canApprove: true,
    canBayar: true,
    canTerima: true,
    canManageUsers: false,
  },
  manager: {
    // Proyek Manager: can view all, add/edit, approve — cannot delete or manage users
    canViewDashboard: true,
    canViewMasterProject: true,
    canViewHutang: true,
    canViewPiutang: true,
    canViewPembayaran: true,
    canViewPenerimaan: true,
    canViewLaporan: true,
    canViewLaporanLanjutan: true,
    canViewLogAktivitas: true,
    canViewUserManagement: false,
    canAdd: true,
    canEdit: true,
    canDelete: false,
    canApprove: true,
    canBayar: true,
    canTerima: true,
    canManageUsers: false,
  },
  staff: {
    // Viewer: read-only access
    canViewDashboard: true,
    canViewMasterProject: true,
    canViewHutang: true,
    canViewPiutang: true,
    canViewPembayaran: true,
    canViewPenerimaan: true,
    canViewLaporan: true,
    canViewLaporanLanjutan: true,
    canViewLogAktivitas: false,
    canViewUserManagement: false,
    canAdd: false,
    canEdit: false,
    canDelete: false,
    canApprove: false,
    canBayar: false,
    canTerima: false,
    canManageUsers: false,
  },
};

export function getPermissions(role: AppRole | undefined | null): RolePermissions {
  if (!role) return PERMISSIONS.staff;
  return PERMISSIONS[role] ?? PERMISSIONS.staff;
}

export function getRoleLabel(role: AppRole | undefined | null): string {
  switch (role) {
    case 'superadmin': return 'Super Admin';
    case 'admin': return 'Admin';
    case 'direktur': return 'Direktur';
    case 'manager': return 'Proyek Manager';
    case 'staff': return 'Viewer';
    default: return 'Viewer';
  }
}
